import React, { useState, useEffect, useCallback } from 'react';
import { FiUserCheck, FiUserX, FiRefreshCw, FiInbox } from 'react-icons/fi';
import apiClient from '../../lib/api/apiClient';
import './UserApprovalsPage.css';

// =============================================
// User Approvals (Site Owner / Site Admin)
// Lists pending access requests (users with approval_status='pending') and lets
// the owner Approve or Reject each. Backed by /api/auth/pending-users +
// /approve-user + /reject-user. While SIGNUP_APPROVAL_REQUIRED is on, new
// self-service signups land here; flip that flag off to open signups publicly.
// =============================================

interface PendingUser {
  userId: number;
  firstName: string;
  lastName: string;
  userName: string;
  email: string;
  signupIp: string | null;
  requestedAt: string | null;
}

const UserApprovalsPage: React.FC = () => {
  const [users, setUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get<{ users: PendingUser[] }>('/auth/pending-users');
      setUsers(res.data.users || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.data?.message || 'Could not load pending requests. Make sure you are signed in as an admin and the backend is running.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (u: PendingUser, kind: 'approve' | 'reject') => {
    setBusyId(u.userId);
    setMsg('');
    try {
      await apiClient.post(`/auth/${kind}-user`, { userId: u.userId });
      setUsers(prev => prev.filter(x => x.userId !== u.userId));
      const who = (u.firstName || u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : u.email;
      setMsg(kind === 'approve' ? `Approved ${who}. They can now sign in.` : `Rejected ${who}.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.data?.message || 'Action failed. Please try again.');
    } finally {
      setBusyId(null);
    }
  }, []);

  const fmtDate = (s: string | null) => {
    if (!s) return '—';
    const d = new Date(s);
    return isNaN(d.getTime()) ? String(s).slice(0, 16) : d.toLocaleString();
  };

  return (
    <div className="ua-page">
      <div className="ua-head">
        <div>
          <h2 className="ua-title"><FiUserCheck size={20} /> User Approvals</h2>
          <p className="ua-sub">New sign-up requests waiting for your approval. Approve to let them sign in; reject to decline.</p>
        </div>
        <button className="ua-refresh" onClick={load} disabled={loading} title="Refresh">
          <FiRefreshCw size={14} className={loading ? 'ua-spin' : ''} /> Refresh
        </button>
      </div>

      {msg && <div className="ua-msg">{msg}</div>}
      {error && <div className="ua-error">{error}</div>}

      {loading ? (
        <div className="ua-empty">Loading…</div>
      ) : users.length === 0 ? (
        <div className="ua-empty"><FiInbox size={28} /><p>No pending requests.</p></div>
      ) : (
        <div className="ua-table-wrap">
          <table className="ua-table">
            <thead>
              <tr><th>Email</th><th>Name</th><th>Requested</th><th>IP</th><th></th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.userId}>
                  <td className="ua-email">{u.email}</td>
                  <td>{(u.firstName || u.lastName) ? `${u.firstName} ${u.lastName}`.trim() : <span className="ua-muted">—</span>}</td>
                  <td className="ua-date">{fmtDate(u.requestedAt)}</td>
                  <td className="ua-muted">{u.signupIp || '—'}</td>
                  <td className="ua-actions">
                    <button className="ua-approve" disabled={busyId === u.userId} onClick={() => act(u, 'approve')}>
                      <FiUserCheck size={14} /> Approve
                    </button>
                    <button className="ua-reject" disabled={busyId === u.userId} onClick={() => act(u, 'reject')}>
                      <FiUserX size={14} /> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserApprovalsPage;
